// @flow

import React, { useEffect } from 'react';
import type { Node } from 'react';
import { connect } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { scrollToTop } from '../../helpers/scrollTo';
import type { FlatError } from '../../helpers/flattenError';
import type { RouteItem } from '../../store/routes';
import {
  getActiveRoute,
  needsLoading as needsLoadingSelector,
  hasError,
  getError,
  isLoading as isLoadingSelector,
  fetchRoute,
} from '../../store/routes';
import { getPreventScrollTop } from '../../store/router';
import type { FixMe } from '../../types/any';

// withRoute will give you:
export type WithRouteProps = {|
  activeRoute: ?RouteItem,
|};

// @TODO: create HoC typing https://justlease.atlassian.net/browse/LISTO-305
const withRoute = () => (WrappedComponent: FixMe) => {
  type OwnProps = {|
    children: Node,
  |};

  type StateProps = {|
    activeRoute: ?RouteItem,
    needsLoading: boolean,
    isLoading: boolean,
    isError: boolean,
    error: ?FlatError,
    preventScrollTop: boolean,
  |};

  type DispatchProps = {|
    fetch: () => *,
  |};

  type Props = {|
    ...OwnProps,
    ...StateProps,
    ...DispatchProps,
  |};

  const WithRoute = ({
    activeRoute,
    needsLoading,
    isLoading,
    isError,
    error,
    fetch,
    ...props
  }: Props) => {
    const { pathname } = useLocation();

    const switchRoute = async () => {
      const { preventScrollTop } = props;
      await fetch();

      if (!preventScrollTop) {
        scrollToTop();
      }
    };

    useEffect(() => {
      switchRoute();
    }, [pathname]);

    if (isError && error) {
      return (
        <div>
          Something went wrong...:
          {error.message}
        </div>
      );
    }

    if (activeRoute === null) {
      return <div>Loading...</div>;
    }

    return <WrappedComponent {...props} activeRoute={activeRoute} />;
  };

  const mapStateToProps = (state): StateProps => ({
    activeRoute: getActiveRoute(state),
    needsLoading: needsLoadingSelector(state),
    isLoading: isLoadingSelector(state),
    isError: hasError(state),
    error: getError(state),
    preventScrollTop: Boolean(getPreventScrollTop(state)),
  });

  const mapDispatchToProps = (dispatch: Function): DispatchProps => ({
    fetch: () => dispatch(fetchRoute()),
  });

  return connect<Props, OwnProps, StateProps, DispatchProps, any, any>(
    mapStateToProps,
    mapDispatchToProps,
  )(WithRoute);
};

export default withRoute;
